/**
 * `shabbat` (§6, D41/D56/D87/D100/**D174**) — the שעון שבת configuration panel. Ported from
 * `prototype/screens/shabbat.js`. **Ships with P8, not after it** (seams §2) — the one P15
 * surface with no product flip condition; the shabbat_panel remote flag (`config/`, R9/D329)
 * is this repo's *engineering* gate on top of that — once a per-stage compile-time table, now
 * one operator write away on every stage.
 *
 * Two things at once, matching the PRD:
 *  (a) the configuration surface — the `hebcal`/`manual` fork and each fork's own fields, `mode`
 *      fixed to `perm-closed` (D87: "פתיחה קבועה" is shown Soon-badged and disabled, never a real
 *      choice), plus **D174's locality picker**. Enable/disable is not a control on this screen at
 *      all: Gate Detail's four-pill segmented control is the single place a gate's state is
 *      chosen, "מתוזמן" lights up from `config.configured`, and **saving here is the only thing
 *      this screen turns on**. D174 deleted the "כיבוי אוטומציית שעון שבת" button and its
 *      confirmation — two off switches for one thing, one of which the pills already implied.
 *  (b) **D246 withdrew D100's last-run surface from this panel, and the seen-write went with it.**
 *      The outcome banner is deleted, so this screen no longer reports the automation's last run
 *      at all — and with nothing shown, nothing may write "seen": marking a failure seen on a load
 *      that never rendered it is a claim about a resident's attention that no pixel supports. The
 *      server side is untouched — `POST /gates/{id}/shabbat/seen` stays deployed, the config read
 *      still carries `unseenFailure`, and `markShabbatSeen()` stays exported from `../shared/api`
 *      with no caller, the client API surface mirroring the server rather than the screens.
 *
 * **The form seeds from a *retained* configuration, not only a running one** (D174). Turning the
 * automation off suspends the record instead of deleting it, so a manager who tapped "רגיל" on
 * Gate Detail and came back finds their own days, times, offsets and city — not the defaults.
 * `config.retained` is the flag; `config.configured` still means "running", and the two differ
 * for exactly that one state.
 *
 * Manager-only. A resident who reaches this route directly gets the same `403` → home redirect
 * `GateDetailScreen`'s `load()` uses for a gate the viewer can no longer see — the server's
 * `requireManager` is the authority; this screen does not re-derive it.
 */
import { useEffect, useRef, useState } from 'react'
import type { GateId, IsoInstant, Weekday } from '@selogate/core'
import { useDeviceCache } from '../../device-cache/context'
import { useSession } from '../../auth/context'
import { NetworkError } from '../../auth/transport'
import { AppBar, ConfirmPopover, FrameOverlay, Icon, SoonBadge } from '../../components'
import { useLocale, useTranslation } from '../../i18n/I18nProvider'
import type { Locale } from '../../i18n/locales'
import { formatDateTime, WEEKDAY_ORDER } from '../../i18n/format'
import { useDelayedIndicator } from '../../hooks/useDelayedIndicator'
import { useDismissOnBack, useNavigation } from '../../router'
import {
  getShabbatConfig,
  getShabbatPreview,
  putShabbatConfig,
  type ShabbatConfig,
  type ShabbatConfigInput,
  type ShabbatPreview,
} from '../shared/api'
import { takeGateHandoff } from '../shared/gateHandoff'
import { setPendingGateToast } from '../shared/pendingToast'
import { SHABBAT_LOCALITIES, localityLabel } from './localities'

/** 🔸 Mirrors `packages/functions/state/config.ts`'s `MAX_OFFSET_NUDGE_MINUTES` — the server is
 * the authority and re-validates; this only keeps the stepper from offering an obviously invalid
 * value. Not imported: `packages/functions/state` is a Lambda package, not browser-bundleable. */
const MAX_OFFSET_NUDGE_MINUTES = 120
const OFFSET_STEP_MINUTES = 5

/**
 * D175 — how long a change waits before it becomes an outbound Hebcal request.
 *
 * The ± steppers are tapped in runs of three or four, and each tap changes the configuration
 * being previewed. Without this, a manager nudging an offset by twenty minutes would issue four
 * requests to a third party for three answers nobody reads.
 *
 * **Exported for `ShabbatScreen.test.tsx` alone, and it is the wait's value rather than a copy of
 * it.** That suite has to outlast this timer before it can see a preview at all, and it did so with
 * a literal `450` that nothing tied to this number — so tuning the debounce upward would have
 * narrowed the margin to nothing and left seven cases asserting on a preview that had not been
 * requested yet. Imported, the two cannot drift.
 */
export const PREVIEW_DEBOUNCE_MS = 400

const DAY_KEY = {
  sun: 'daySun',
  mon: 'dayMon',
  tue: 'dayTue',
  wed: 'dayWed',
  thu: 'dayThu',
  fri: 'dayFri',
  sat: 'daySat',
} as const satisfies Record<Weekday, string>

type ScheduleFork = 'hebcal' | 'manual'

/** The form's own state, seeded from a loaded/saved `ShabbatConfig` and otherwise left at these
 * defaults (§6: hebcal by default). Enabling/disabling is no longer a field the form carries —
 * Gate Detail's segmented control reflects `config.configured` directly, and this screen's own
 * "Turn off" action (below) is gated on the same flag. */
interface FormState {
  readonly schedule: ScheduleFork
  readonly entryOffset: number
  readonly exitOffset: number
  readonly days: readonly Weekday[]
  readonly entryTime: string
  readonly exitTime: string
  /**
   * D174 — `'auto'` means "keep resolving from the gate's address", and it is what the request
   * omits rather than what it sends. A `geonameid` here is a city the manager named themselves.
   */
  readonly geonameid: number | 'auto'
}

/**
 * **D175 opens the panel on `manual`, and §6's "hebcal by default" is unchanged by that.**
 *
 * The default the PRD names is the *server's*: a `hebcal` gate is the intended configuration, and
 * `PUT` still writes whichever fork the request names. What changed is which fork an empty form
 * is showing while the manager is still deciding — and `hebcal`'s fields (a city, two offsets)
 * are the ones that mean nothing until they are read together with the times they produce, while
 * `manual`'s days and clock times say what they do on their own. A manager who wants the Hebrew
 * calendar taps one pill and D175's preview answers immediately; a manager who does not, never
 * sees a fork they were not going to use.
 *
 * **This is the empty form only.** A gate with a retained configuration seeds from it
 * (`formFromConfig`), so nothing here can move a configured gate off its own fork.
 */
const DEFAULT_FORM: FormState = {
  schedule: 'manual',
  entryOffset: 0,
  exitOffset: 0,
  days: ['fri', 'sat'],
  entryTime: '18:30',
  exitTime: '20:00',
  geonameid: 'auto',
}

/**
 * Seed the form from whatever the server retained.
 *
 * **`retained`, not `configured`** (D174): a suspended record is not running and is still the
 * manager's own configuration. Reading `configured` here — as this did before D174 — would hand
 * back the defaults every time someone turned the automation off and on again, quietly discarding
 * the days and times they had set.
 *
 * `geonameid` seeds from `geo` **only when the server says a manager chose it**. Seeding it from
 * `geo` unconditionally would turn every auto-resolved city into a frozen manual choice on the
 * next save, which is the one thing the automatic path must not become by accident.
 */
function formFromConfig(config: ShabbatConfig): FormState {
  if (!config.retained) return DEFAULT_FORM
  return {
    schedule: config.schedule ?? 'hebcal',
    entryOffset: config.entryOffset ?? 0,
    exitOffset: config.exitOffset ?? 0,
    days:
      config.days !== undefined && config.days.length > 0
        ? (config.days as readonly Weekday[])
        : DEFAULT_FORM.days,
    entryTime: config.entryTime ?? DEFAULT_FORM.entryTime,
    exitTime: config.exitTime ?? DEFAULT_FORM.exitTime,
    geonameid: config.geoSource === 'manual' && config.geo !== undefined ? config.geo : 'auto',
  }
}

/**
 * 🔸 D174 — the manual fork's one refusal-free warning: an exit clock at or before the entry
 * clock is the *next day's* (`packages/functions/state/windows.ts` resolves it that way), which
 * is a real and useful configuration — Friday 18:00 → Saturday 17:00 — and also exactly what a
 * typo looks like. **It warns and does not block**: the server accepts both, and a UI that
 * refused one would take a working configuration away to catch a mistake it cannot identify.
 */
function crossesMidnight(entryTime: string, exitTime: string): boolean {
  return exitTime <= entryTime
}

/**
 * Apply a manager's nudge to an instant the server answered with.
 *
 * 🔸 Mirrors `packages/functions/state/windows.ts`, which defines a nudge as exactly this —
 * `edge.atMs + entryNudge` — and is the authority. **It is arithmetic on an answer, not a second
 * answer**: nothing here computes a candle-lighting time, and a drift between the two would show
 * as a visibly wrong clock in the confirmation rather than as a silent one.
 */
function nudged(at: IsoInstant | undefined, minutes: number): IsoInstant | undefined {
  if (at === undefined) return undefined
  if (minutes === 0) return at
  return new Date(Date.parse(at) + minutes * 60_000).toISOString() as IsoInstant
}

/** The inverse, for the one place a *stored* window has to be shown as the calendar's own. */
function unnudged(at: IsoInstant | undefined, minutes: number | undefined): IsoInstant | undefined {
  return nudged(at, -(minutes ?? 0))
}

/**
 * **G-P8-a — is the confirmed window already running?** A save made inside one closes the gate
 * the moment it lands and holds it until the window's exit (the owner's option A, in
 * `packages/functions/state/routes.ts`), so the confirmation must say *now* rather than quote an
 * entry instant that has already passed.
 *
 * 🔸 Mirrors `armWindow`'s `inProgress` (`packages/functions/state/automation.ts`) — entry at or
 * before now — over the **nudged** pair, which is what the server compares, plus the exit still
 * ahead, which the server gets for free from `nextWindow` never returning a finished window. Like
 * `nudged`, it is a comparison on an answer the server gave and not a second answer.
 *
 * **`hebcal` only, and the gap is stated rather than closed.** The manual fork's dialog describes
 * days and clock times, never instants, and whether now falls inside one is `nextManualWindow`'s
 * walk — the gate's zone, D67's resolution of a wall time, the midnight shift and the one-day
 * look-back — which is a Lambda package this bundle cannot import. A second copy of that walk here
 * is the drift this file's other two mirrors are kept small to avoid, so a manual save made during
 * its own window still reads as the ordinary summary.
 *
 * **The release half of that gap is NOT left silent.** A save whose window is not running releases a
 * gate the automation holds (`releaseEndedLatch`), and the owner approved that on condition the panel
 * says so. Where this function cannot answer — the manual fork, and a `hebcal` save with no times —
 * `ScheduleSummary` states it conditionally (`confirmReleaseIfNotNow`) rather than guessing either way.
 */
function windowInProgress(entryAt: IsoInstant, exitAt: IsoInstant, nowMs: number): boolean {
  return Date.parse(entryAt) <= nowMs && Date.parse(exitAt) > nowMs
}

function clampOffset(value: number): number {
  return Math.max(-MAX_OFFSET_NUDGE_MINUTES, Math.min(MAX_OFFSET_NUDGE_MINUTES, value))
}

export function ShabbatScreen({ gateId }: { readonly gateId: GateId }): JSX.Element {
  const session = useSession()
  const deviceCache = useDeviceCache()
  const navigate = useNavigation()
  const { t } = useTranslation('shabbat')
  const { t: tCommon } = useTranslation('common')
  const { locale } = useLocale()

  const offline = !session.online
  const cached = deviceCache.gates.find((g) => g.gateId === gateId)
  const gateName = cached?.alias ?? cached?.defaultName ?? ''
  const locationFormatted = cached?.locationFormatted ?? ''

  const [loading, setLoading] = useState(true)
  const [loadFailed, setLoadFailed] = useState(false)
  const [config, setConfig] = useState<ShabbatConfig | undefined>(undefined)
  const [form, setForm] = useState<FormState>(DEFAULT_FORM)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | undefined>(undefined)
  const [daysError, setDaysError] = useState(false)
  /**
   * D175 — the confirmation the save now passes through. **Every save opens it**, not only the
   * ones with something unusual in them: the dialog's job is to state the configuration in
   * calendar terms — which days, which clock times, which way the offsets point, and that the
   * release is the *next* day — and a configuration is exactly as worth reading when it is
   * ordinary. Opened from the manager's own tap, which is what D145 requires of any overlay that
   * expects Android back to close it.
   *
   * **It stays open across the write, not only before it — up to the point the write answers.**
   * A validation error or a dropped connection lands here, in `ConfirmPopover`'s own `error` slot,
   * so a retry is one more tap on the same button. **Success skips straight past this screen**:
   * the manager already read the configuration back in this dialog before confirming it, so
   * there is nothing left for either the dialog or this screen to say — `save()` closes it, hands
   * a success message to `setPendingGateToast()`, and routes straight to Gate Detail, which is
   * the one that shows it (`ButterPop`'s `ToastHost`, there).
   */
  const [confirming, setConfirming] = useState(false)
  const [preview, setPreview] = useState<ShabbatPreview | undefined>(undefined)
  /**
   * The preview's own phase, separate from the configuration's. `failed` covers both halves of
   * "we cannot say" — the request did not land, or it landed and the feed had nothing — because
   * the manager's answer to each is the same and neither is a claim about the gate.
   */
  const [previewPhase, setPreviewPhase] = useState<'idle' | 'loading' | 'ready' | 'failed'>('idle')

  useEffect(() => {
    let cancelled = false
    async function load(): Promise<void> {
      setLoading(true)
      setLoadFailed(false)
      try {
        /*
         * **R5 step 6 — the Access page prefetches this on entry, so the panel usually opens with
         * it already in hand.** Only a config or the `403`/`404` that sends this screen home is
         * ever deposited; a failed prefetch deposits nothing and falls through to the read below,
         * because handing a screen a connectivity failure it could simply have retried would make
         * the prefetch able to make things worse. Read-once, so a retry re-reads.
         */
        const handed = takeGateHandoff(gateId, 'shabbat')
        if (handed !== undefined) {
          if (handed.kind === 'gone') {
            navigate.resetTo({ id: 'home' })
            return
          }
          setConfig(handed.config)
          setForm(formFromConfig(handed.config))
          return
        }
        const result = await getShabbatConfig(session.request, gateId)
        if (cancelled) return
        if (result.ok) {
          setConfig(result.data)
          setForm(formFromConfig(result.data))
        } else if (result.status === 403 || result.status === 404) {
          navigate.resetTo({ id: 'home' })
          return
        } else {
          setLoadFailed(true)
        }
      } catch (error) {
        if (!(error instanceof NetworkError)) throw error
        if (!cancelled) setLoadFailed(true)
      } finally {
        if (!cancelled) setLoading(false)
      }
    }
    void load()
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId])

  /**
   * **D175 — the times, before anything is saved.**
   *
   * The כניסת שבת/יציאת שבת rows used to be filled from the *stored* window, which exists only
   * after a save. Selecting "לוח עברי" therefore showed nothing at all until the manager had
   * committed a configuration whose whole point was the times they could not yet see — and
   * changing the city or nudging an offset changed nothing on screen either.
   *
   * Four things this effect is careful about:
   *
   * - **It runs on the `hebcal` fork only.** `manual`'s window is the manager's own days and
   *   clock times; asking a server what they are would be asking it to read the form back.
   * - **It does not depend on the offsets, and the rows do not move with them.** What this
   *   answers is *the calendar's* candle-lighting and havdalah — the times a nudge is measured
   *   *away from* — so a row that slid with the stepper would leave nothing on screen saying
   *   what the ±10 was relative to. The nudged pair belongs to the save confirmation. It also
   *   means a run of stepper taps issues no requests at all.
   * - **The response is stamped and stale answers are dropped.** Changing the city twice quickly
   *   issues two previews, and they can land in any order — an out-of-order landing would show
   *   the time for a city the manager has already moved past, which is the exact class of quiet
   *   wrongness the preview exists to remove.
   * - **Offline does not clear what is on screen.** The stored window is still the last thing the
   *   server actually computed, so the rows fall back to it rather than to nothing (see
   *   `shownEntryAt` below); the panel already says it is offline.
   */
  const previewSeq = useRef(0)
  useEffect(() => {
    if (form.schedule !== 'hebcal' || offline || loading) {
      setPreviewPhase('idle')
      setPreview(undefined)
      return
    }
    const seq = previewSeq.current + 1
    previewSeq.current = seq
    setPreviewPhase('loading')
    // Debounced: a select can be scrolled through several cities before one is settled on, and
    // each would otherwise be an outbound Hebcal call on the manager's behalf. Short enough that
    // a single deliberate change still feels answered.
    const timer = window.setTimeout(() => {
      void (async () => {
        try {
          const result = await getShabbatPreview(session.request, gateId, {
            ...(form.geonameid === 'auto' ? {} : { geonameid: form.geonameid }),
          })
          if (previewSeq.current !== seq) return
          if (result.ok && result.data !== undefined) {
            setPreview(result.data)
            setPreviewPhase(result.data.entryAt === undefined ? 'failed' : 'ready')
          } else {
            setPreview(undefined)
            setPreviewPhase('failed')
          }
        } catch (error) {
          if (!(error instanceof NetworkError)) throw error
          if (previewSeq.current !== seq) return
          setPreview(undefined)
          setPreviewPhase('failed')
        }
      })()
    }, PREVIEW_DEBOUNCE_MS)
    return () => window.clearTimeout(timer)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gateId, form.schedule, form.geonameid, offline, loading])

  function toggleDay(day: Weekday): void {
    setDaysError(false)
    setForm((current) => ({
      ...current,
      days: current.days.includes(day) ? current.days.filter((d) => d !== day) : [...current.days, day],
    }))
  }

  /**
   * **The save button opens the confirmation; it does not write** (D175).
   *
   * The days check runs *here*, before the dialog, and not inside it: a confirmation that had to
   * describe "ימים: —" would be asking the manager to approve a configuration that does not
   * exist. Everything else the dialog can state exactly, so everything else is the dialog's.
   */
  function requestSave(): void {
    if (form.schedule === 'manual' && form.days.length === 0) {
      setDaysError(true)
      return
    }
    setSaveError(undefined)
    setDaysError(false)
    setConfirming(true)
  }

  async function save(): Promise<void> {
    if (form.schedule === 'manual' && form.days.length === 0) {
      setDaysError(true)
      return
    }
    setSaving(true)
    setSaveError(undefined)
    setDaysError(false)
    const body: ShabbatConfigInput =
      form.schedule === 'hebcal'
        ? {
            schedule: 'hebcal',
            mode: 'perm-closed',
            ...(form.entryOffset === 0 ? {} : { entryOffset: form.entryOffset }),
            ...(form.exitOffset === 0 ? {} : { exitOffset: form.exitOffset }),
            // D174 — omitted for `'auto'`, which is how the server is told to keep resolving from
            // the address. Sending the auto-resolved id back would silently promote it to a
            // manual choice and pin the gate to a city nobody picked.
            ...(form.geonameid === 'auto' ? {} : { geonameid: form.geonameid }),
          }
        : {
            schedule: 'manual',
            mode: 'perm-closed',
            days: form.days,
            entryTime: form.entryTime,
            exitTime: form.exitTime,
          }
    try {
      const result = await putShabbatConfig(session.request, gateId, body)
      if (result.ok) {
        // The manager already read this configuration back in the dialog they just confirmed —
        // there is nothing left to say on this screen. `setPendingGateToast` hands the one thing
        // worth saying to whatever screen `resetTo` is about to land on, and Gate Detail is where
        // this write's own effect (the "מתוזמן" pill lighting up) is visible anyway.
        //
        // **A save can move the gate's state, not only its schedule** — one inside its window
        // latches the gate now, and one that ends the window the automation holds releases it now.
        // Gate Detail reads that off its own `GET`, but Home reads `operatingState` off the device
        // cache, so without this the tile keeps its pre-save state until the next unrelated
        // refresh — the gap `AccessScreen`'s `setMode` closes the same way.
        void deviceCache.refresh()
        setPendingGateToast(gateId, t('saveSuccess'))
        navigate.resetTo({ id: 'gate', gateId })
        return
      } else if (result.field === 'days') {
        // A different form section owns this error — the days chips, not the confirmation — so
        // the dialog steps aside rather than stating a problem it has no control for.
        setConfirming(false)
        setDaysError(true)
      } else {
        setSaveError(t('saveError'))
      }
    } catch (error) {
      if (!(error instanceof NetworkError)) throw error
      setSaveError(t('saveErrorNetwork'))
    } finally {
      setSaving(false)
    }
  }

  // Registered before the early returns below, because a hook cannot be. D145's contract is that
  // the entry is pushed when `confirming` becomes true and unwound however the dialog closes.
  // **Not dismissible while `saving`** — mirrors `DevicesScreen`'s revoke dialog: a back tap
  // cannot cancel a write already sent, so a dismissed-looking dialog over one still in flight
  // would be the screen lying about it.
  useDismissOnBack('shabbat:save-confirm', confirming, () => setConfirming(false), {
    dismissible: !saving,
  })

  /**
   * **The city the *address* resolves to — never the one the manager chose** (D175).
   *
   * The auto option's label is this, so it has to answer the question the option asks: "if I do
   * not name a city, which one is used?" The preview answers it live while the automatic path is
   * selected; a preview taken *with* a chosen city answers a different question, which is why it
   * is only read when `form.geonameid === 'auto'`. Falling back to the stored `geo` is likewise
   * conditional on the record not having been a manual choice — that value is the manager's city,
   * not the address's.
   */
  const addressCity =
    form.geonameid === 'auto' && preview?.geo !== undefined
      ? localityLabel(preview.geo, locale)
      : config?.geoSource === 'manual'
        ? undefined
        : localityLabel(config?.geo, locale)

  /**
   * **What the entry/exit rows show: the calendar's own times, never the nudged ones** (D175).
   *
   * These two rows are what an offset is measured *away from*, so they hold still while the
   * stepper moves — a row that slid with it would leave nothing on screen saying what "10 דקות
   * לפני כניסה" was before. The nudged pair is stated once, in the save confirmation.
   *
   * Online that is simply what the preview answers with, since it is asked without offsets.
   * **The stored window is the fallback whenever no preview is available** — offline, and equally
   * when one was asked for and could not be computed. It is still the last pair the server
   * actually produced, and showing it beats showing nothing on a screen whose whole subject is
   * when the gate closes. It **has to be un-nudged** first, because the server stamped it *with*
   * the offsets: `nextEntryAt` minus `entryOffset` recovers the candle-lighting exactly, a nudge
   * being a whole number of minutes and nothing else (`windows.ts`). Falling back to it unmodified
   * would make the same row mean two different things depending on connectivity, which is the
   * sort of difference nobody would ever see reported.
   */
  const storedEntryAt = unnudged(config?.nextEntryAt, config?.entryOffset)
  const storedExitAt = unnudged(config?.nextExitAt, config?.exitOffset)
  const shownEntryAt = preview?.entryAt ?? (offline || previewPhase === 'failed' ? storedEntryAt : undefined)
  const shownExitAt = preview?.exitAt ?? (offline || previewPhase === 'failed' ? storedExitAt : undefined)
  /**
   * **`idle` is pending, not failed** — and getting that wrong put "לא ניתן לחשב כעת" on screen
   * for a frame every time the fork was selected, because React renders the new fork before the
   * effect that asks about it runs. A phase that means *we have not asked yet* must never render
   * as an answer; only a request that came back and produced no times is a failure, and even then
   * only when there is no stored window to fall back to.
   */
  const previewPending = previewPhase === 'idle' || previewPhase === 'loading'
  /** M7 — see the loading branch below. The preview's own pending state is untouched: it renders
      last-known times rather than a spinner, which is a stronger answer than a delayed one. */
  const slowLoad = useDelayedIndicator(loading)
  // Whether the address's city is a fallback, from whichever source is answering right now.
  const addressFallback =
    form.geonameid === 'auto' && preview !== undefined
      ? preview.geoFallback === true
      : config?.geoFallback === true

  if (loading) {
    return (
      <>
        <AppBar title={t('title', { name: gateName })} onBack={() => navigate.back()} />
        {/* R5 step 7 (M7) — the Access page prefetches this config on entry, so the panel usually
            opens with it in hand and this branch is never reached. Where it is, the wait is a
            fraction of a second and the word is held back for it. The box is kept and only its
            text is delayed: `loadFailed` renders its own error immediately, and a screen whose
            height jumped when the word arrived would be a second flash. */}
        <div style={{ padding: 40, textAlign: 'center', color: 'var(--muted)' }}>
          {slowLoad ? tCommon('loading') : ''}
        </div>
      </>
    )
  }

  if (loadFailed && config === undefined) {
    return (
      <>
        <AppBar title={t('title', { name: gateName })} onBack={() => navigate.back()} />
        <div className="gate-block">{t('loadError')}</div>
      </>
    )
  }

  return (
    <>
      <AppBar title={t('title', { name: gateName })} onBack={() => navigate.back()} />
      <main className="gate-detail">
        {offline ? <div className="gate-block">{t('offlineNotice')}</div> : null}

        <section className="gate-panel shabbat-panel">
          <h2 className="gate-panel-title">
            <Icon name="clock" size={16} /> {t('sectionTitle')}
          </h2>

          {/* D174 removed the three `gate-mode-label` headings — "מצב יעד", "מקור לוח הזמנים",
              "ימים". Each named a row whose own controls already say what it is, and stacked they
              read as a form with more structure than choices. **The `aria-label` on each group
              stays**: the heading was the visible half of a label a screen reader still needs, and
              deleting the accessible half with it would have been a different change. */}
          <div className="gate-mode-block">
            {/* D87 — "סגירה קבועה" is the only target Stage 1 actually latches; "פתיחה קבועה" is
                shown as a Soon-badged, disabled option rather than absent — a preview of what's
                coming, never a real choice. A target the command path refuses must still never be
                configurable, so it carries no `onClick`. */}
            <div className="gate-mode-options" role="group" aria-label={t('targetStateLabel')}>
              <span className="shabbat-pill selected" aria-pressed="true">
                {t('targetPermClosed')}
              </span>
              <button className="shabbat-pill" type="button" aria-pressed="false" disabled>
                {t('targetPermOpen')}
                <SoonBadge />
              </button>
            </div>
            <p className="gate-remote-warning">{t('remoteWarning')}</p>
          </div>

          <div className="gate-mode-block">
            <div className="gate-mode-options" role="group" aria-label={t('sourceLabel')}>
              <button
                className={`shabbat-pill${form.schedule === 'hebcal' ? ' selected' : ''}`}
                type="button"
                aria-pressed={form.schedule === 'hebcal'}
                disabled={offline}
                onClick={() => setForm((current) => ({ ...current, schedule: 'hebcal' }))}
              >
                {t('sourceHebcal')}
              </button>
              <button
                className={`shabbat-pill${form.schedule === 'manual' ? ' selected' : ''}`}
                type="button"
                aria-pressed={form.schedule === 'manual'}
                disabled={offline}
                onClick={() => setForm((current) => ({ ...current, schedule: 'manual' }))}
              >
                {t('sourceManual')}
              </button>
            </div>
          </div>

          {form.schedule === 'hebcal' ? (
            <div className="gate-mode-block">
              {/*
                **D174 — the locality is a choice, not a read-only line.**

                It used to render the gate's address, which is not what Hebcal is asked: the
                server resolves that text to a city and asks for the city (D108 — never the pin).
                When the text does not match, the answer is Tel Aviv and **nothing says so except
                `geoFallback`** — so a gate in an unrecognised street kept another city's
                candle-lighting until somebody noticed. This is the manager's way to say which
                city, without waiting for P17's address inputs and without sending a coordinate.

                `'auto'` is a real option and the default, not a placeholder: it is the only value
                that leaves the gate tracking its address. The resolved city is shown beneath it,
                because "automatic" that does not say *what* it chose is the same silence again.
              */}
              <div className="shabbat-locality">
                <label className="shabbat-locality-label" htmlFor="shabbat-locality">
                  {t('localityLabel')}
                </label>
                <select
                  id="shabbat-locality"
                  className="input shabbat-locality-select"
                  value={form.geonameid === 'auto' ? 'auto' : String(form.geonameid)}
                  disabled={offline}
                  onChange={(e) =>
                    setForm((current) => ({
                      ...current,
                      geonameid: e.target.value === 'auto' ? 'auto' : Number(e.target.value),
                    }))
                  }
                >
                  {/*
                    **D175 — the automatic option names the city, because that is what is sent.**

                    It used to read "אוטומטי — לפי כתובת השער", which describes a mechanism rather
                    than an answer: the manager still could not tell *which* city their gate was
                    following, and the one case where that matters most — an unrecognised address
                    silently taking Tel Aviv — looked identical to every other. D175's preview
                    resolves the locality before anything is saved, so the option can say it.

                    The address is the qualifier, not the value: what reaches Hebcal is the city
                    (D108), and the address is how it was arrived at. When nothing has resolved
                    one yet — offline, or the read did not land — the option falls back to naming
                    the address alone, which is true and stops short of claiming a city.
                  */}
                  <option value="auto">
                    {addressCity !== undefined
                      ? t('localityFromAddressCity', { city: addressCity })
                      : t('localityFromAddress', {
                          address: locationFormatted || t('locationUnknown'),
                        })}
                  </option>
                  {SHABBAT_LOCALITIES.map((locality) => (
                    <option key={locality.geonameid} value={String(locality.geonameid)}>
                      {locale === 'he' ? locality.labelHe : locality.labelEn}
                    </option>
                  ))}
                </select>
              </div>
              {/* The one thing the option above cannot say for itself: that the city naming it
                  is the *fallback* rather than a match. D175 deleted the other two hints — the
                  option now carries "resolved to X" and "will resolve from this address" — and
                  kept this one, because a fallback is a wrong city waiting to happen and the
                  option would present it in exactly the same words as a correct one. */}
              {form.geonameid === 'auto' && addressFallback ? (
                <p className="gate-remote-warning">{t('geoFallbackHint')}</p>
              ) : null}
              {/* **D175 — the times are shown for the configuration on screen, not for the one
                  last saved.** They arrive from the preview read, which computes them exactly as
                  the save would and stores nothing, so selecting "לוח עברי" or naming a city
                  answers straight away. The row keeps its place while the answer is in flight
                  rather than collapsing and reappearing: this is the block the manager is
                  reading, and a layout that jumps is harder to read than one that says "מחשב…"
                  for a moment.

                  **They sit *below* the city, and the order is the argument.** The city is the
                  input and these two lines are its answer — printing the answer first left the
                  control that produced it further down the screen, so the one line that explains
                  why the times moved was the one a manager had to scroll to find. */}
              <div className="gate-detail-row">
                <span className="gate-row-label">{t('nextEntryLabel')}</span>
                <span className="gate-row-value">
                  {shownEntryAt === undefined
                    ? previewPending
                      ? t('previewComputing')
                      : t('previewUnavailable')
                    : formatDateTime(shownEntryAt, locale, {
                        weekday: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                </span>
              </div>
              <div className="gate-detail-row">
                <span className="gate-row-label">{t('nextExitLabel')}</span>
                <span className="gate-row-value">
                  {shownExitAt === undefined
                    ? previewPending
                      ? t('previewComputing')
                      : t('previewUnavailable')
                    : formatDateTime(shownExitAt, locale, {
                        weekday: 'short',
                        hour: '2-digit',
                        minute: '2-digit',
                        hour12: false,
                      })}
                </span>
              </div>
              <OffsetRow
                label={t('entryOffsetLabel')}
                value={form.entryOffset}
                caption={offsetCaption(t, 'entry', form.entryOffset)}
                disabled={offline}
                decreaseLabel={t('decreaseOffset')}
                increaseLabel={t('increaseOffset')}
                onAdjust={(delta) =>
                  setForm((current) => ({
                    ...current,
                    entryOffset: clampOffset(current.entryOffset + delta),
                  }))
                }
              />
              <OffsetRow
                label={t('exitOffsetLabel')}
                value={form.exitOffset}
                caption={offsetCaption(t, 'exit', form.exitOffset)}
                disabled={offline}
                decreaseLabel={t('decreaseOffset')}
                increaseLabel={t('increaseOffset')}
                onAdjust={(delta) =>
                  setForm((current) => ({ ...current, exitOffset: clampOffset(current.exitOffset + delta) }))
                }
              />
            </div>
          ) : (
            <div className="gate-mode-block">
              <div className="shabbat-day-row" role="group" aria-label={t('daysLabel')}>
                {WEEKDAY_ORDER.map((day) => (
                  <button
                    key={day}
                    type="button"
                    className={`shabbat-day-chip${form.days.includes(day) ? ' selected' : ''}`}
                    aria-pressed={form.days.includes(day)}
                    disabled={offline}
                    onClick={() => toggleDay(day)}
                  >
                    {t(DAY_KEY[day])}
                  </button>
                ))}
              </div>
              {daysError ? <p className="popover-error">{t('daysError')}</p> : null}
              <div className="shabbat-time-row">
                <label>
                  <span>{t('entryTimeLabel')}</span>
                  <input
                    type="time"
                    dir="ltr"
                    className="input"
                    value={form.entryTime}
                    disabled={offline}
                    onChange={(e) => setForm((current) => ({ ...current, entryTime: e.target.value }))}
                  />
                </label>
                <label>
                  <span>{t('exitTimeLabel')}</span>
                  <input
                    type="time"
                    dir="ltr"
                    className="input"
                    value={form.exitTime}
                    disabled={offline}
                    onChange={(e) => setForm((current) => ({ ...current, exitTime: e.target.value }))}
                  />
                </label>
              </div>
              {/* D174 — a warning, never a refusal. An exit clock at or before the entry clock is
                  the next day's, which is a working configuration (Friday 18:00 → Saturday 17:00)
                  and also what a typo looks like. The server accepts both, so this says which one
                  the manager just described and lets them decide. */}
              {crossesMidnight(form.entryTime, form.exitTime) ? (
                <p className="shabbat-crosses-midnight">{t('crossesMidnight')}</p>
              ) : null}
            </div>
          )}

          <div className="form-row">
            <button
              className="btn-primary"
              type="button"
              disabled={saving || offline}
              onClick={() => requestSave()}
            >
              {saving ? tCommon('loading') : t('saveAction')}
            </button>
          </div>
          {/*
            **D174 moved the standing note below the button.** It describes what saving *commits
            to* — a weekly refresh, Chagim included, and a manager's manual action always winning —
            rather than how to fill a field in, so above the fields it read as another instruction
            to work through before getting to the point. Below the button it is the footnote it
            always was.

            Both forks' notes moved, not only `hebcalHint`: the slot is "the note under the
            button", and leaving `manualHint` above the fields would have made the placement
            depend on which fork you were looking at.
          */}
          <p className="shabbat-footnote">{form.schedule === 'hebcal' ? t('hebcalHint') : t('manualHint')}</p>
        </section>
      </main>

      {confirming ? (
        <FrameOverlay>
          <ConfirmPopover
            title={t('confirmTitle')}
            body={
              <ScheduleSummary
                form={form}
                // **The one place the nudged pair appears** (D175). The rows above hold the
                // calendar's times; this states what the gate will actually do with them.
                entryAt={nudged(shownEntryAt, form.entryOffset)}
                exitAt={nudged(shownExitAt, form.exitOffset)}
                city={form.geonameid === 'auto' ? addressCity : localityLabel(form.geonameid, locale)}
                // **The READ's value — the gate before this save.** The dialog describes what
                // confirming is about to do, and `PUT`'s echo carries the state after it. `config`
                // is only ever set from `GET` (or the Access page's prefetch of it); `save()` routes
                // away on success and never adopts the echo, and it must stay that way.
                latchedByAutomation={config?.latchedByAutomation === true}
                locale={locale}
                t={t}
              />
            }
            {...(saveError !== undefined ? { error: saveError } : {})}
            cancelLabel={tCommon('cancel')}
            confirmLabel={saving ? tCommon('loading') : t('confirmAction')}
            confirmDisabled={saving}
            onCancel={() => {
              if (saving) return
              setConfirming(false)
            }}
            onConfirm={() => void save()}
          />
        </FrameOverlay>
      ) : null}
    </>
  )
}

/**
 * **D175's confirmation body — the configuration read back as days and hours.**
 *
 * The panel is a form: two pills, a select, two steppers or seven chips and two clocks. Read
 * downwards it never states the thing it configures, and three of its fields are the ones a
 * manager is most likely to have meant differently than they set:
 *
 * - **the midnight crossing** — an exit clock at or before the entry clock is the *next* day's,
 *   which is a working configuration and also exactly what a typo looks like (D174). The panel
 *   warns; this says which day the gate is actually released on.
 * - **the offsets** — `hebcal`'s ± steppers are the difference between latching at candle-lighting
 *   and latching ten minutes before it, and the number in the stepper is unsigned by design.
 * - **the city** — which is what "לפי כתובת השער" resolves to, and the one value nothing in the
 *   form is authoritative about.
 *
 * The `hebcal` half quotes D175's previewed instants **with the offsets applied**, and it is the
 * only surface that does: the panel's two rows are the calendar's own times, so this is where a
 * manager finally sees 18:20 rather than 18:30 and the words "10 דקות לפני כניסה" beside it. When
 * the preview could not be computed it says so and **still offers the save**: refusing would make
 * an unreachable third party a reason a manager cannot configure their own gate, and the
 * configuration itself is unaffected by it. When the previewed window is **already running**, the
 * entry line becomes *closes now, until the release* (`windowInProgress`, G-P8-a): the save itself
 * is what closes the gate then, and a past entry instant quoted as "Closes:" hid exactly that. When
 * the window is **not** running and the automation holds the gate latched, the save releases it at
 * once, and the dialog leads with that (`confirmReleaseNow`) — the mirror line.
 *
 * **Where the client cannot tell whether the new window covers now, a latched gate still gets a
 * warning — a conditional one** (`confirmReleaseIfNotNow`). That is the manual fork always (its
 * window is `nextManualWindow`'s walk, which this bundle refuses to copy) and the `hebcal` fork when
 * no times could be computed. The server releases in exactly the case the sentence names, and the
 * owner approved that release on condition the panel says "opens now"; a dialog that said nothing
 * there let a manager release a gate mid-Shabbat unwarned. **`latchedByAutomation` false or absent
 * adds nothing**: absent is a server that cannot say the latch is the automation's, and the server
 * releases nothing else.
 *
 * **It carries no restatement of what `perm-closed` covers.** D82's physical-remote caveat is a
 * standing property of the target state, not of this schedule, and the panel prints it under the
 * state pills where the manager chose it — repeating it here padded a dialog whose whole value is
 * that it is short enough to read.
 *
 * Rendered as blocks inside `ConfirmPopover`'s `<p>`, so every element here is inline by tag and
 * `display: block` by class — a `<div>` or a `<ul>` would be invalid inside a paragraph.
 */
function ScheduleSummary({
  form,
  entryAt,
  exitAt,
  city,
  latchedByAutomation,
  locale,
  t,
}: {
  readonly form: FormState
  readonly entryAt: IsoInstant | undefined
  readonly exitAt: IsoInstant | undefined
  readonly city: string | undefined
  /** The server's projection of "latched, and the automation holds it" — see `ShabbatConfig`. */
  readonly latchedByAutomation: boolean
  readonly locale: Locale
  readonly t: ReturnType<typeof useTranslation<'shabbat'>>['t']
}): JSX.Element {
  const when = (at: IsoInstant): string =>
    formatDateTime(at, locale, {
      weekday: 'long',
      day: 'numeric',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })

  if (form.schedule === 'hebcal') {
    const nowMs = Date.now()
    const pair =
      entryAt === undefined || exitAt === undefined ? undefined : (
        <>
          <span className="shabbat-confirm-line">{t('confirmEntryAt', { when: when(entryAt) })}</span>
          <span className="shabbat-confirm-line">{t('confirmExitAt', { when: when(exitAt) })}</span>
        </>
      )
    return (
      <>
        <span className="shabbat-confirm-line">
          {city === undefined ? t('confirmHebcalLine') : t('confirmHebcalLineCity', { city })}
        </span>
        {entryAt === undefined || exitAt === undefined ? (
          <>
            <span className="shabbat-confirm-line muted">{t('confirmTimesUnavailable')}</span>
            {/* With no times the client cannot tell a running window from an ended one, so the
                release the server makes for the latter is stated conditionally rather than dropped. */}
            {latchedByAutomation ? (
              <span className="shabbat-confirm-line">{t('confirmReleaseIfNotNow')}</span>
            ) : null}
          </>
        ) : windowInProgress(entryAt, exitAt, nowMs) ? (
          // G-P8-a — one line in place of the pair: the entry has passed, so confirming is what
          // closes the gate, and the release instant is the only time left worth quoting. A
          // separate "Releases" line under it would state that instant twice.
          <span className="shabbat-confirm-line">{t('confirmEntryNow', { when: when(exitAt) })}</span>
        ) : latchedByAutomation ? (
          /*
           * G-P8-a's counterpart (`releaseEndedLatch`, `packages/functions/state/fire.ts`): a save
           * whose window is NOT running releases a gate the automation holds, at once — a manager
           * cutting the exit offset until the adjusted havdalah is behind them, or naming a city
           * whose havdalah has passed. `latchedByAutomation` is the server's projection and never
           * inferred here: a gate a manager closed by hand after the automation's entry looks the
           * same from `operatingState` and `lastRun`, and the server leaves that one closed.
           *
           * **The pair is quoted beneath only while it is still ahead** — that window is the one
           * the gate closes on again. One whose adjusted exit has passed is not what the server
           * arms next, and quoting it was the dialog stating past times as a schedule.
           */
          <>
            <span className="shabbat-confirm-line">{t('confirmReleaseNow')}</span>
            {Date.parse(exitAt) > nowMs ? pair : null}
          </>
        ) : (
          pair
        )}
        <span className="shabbat-confirm-line detail">{offsetCaption(t, 'entry', form.entryOffset)}</span>
        <span className="shabbat-confirm-line detail">{offsetCaption(t, 'exit', form.exitOffset)}</span>
      </>
    )
  }

  // Sunday-first (§11) and in the panel's own order, so the list reads as the chips look rather
  // than in the order they happened to be tapped.
  const days = WEEKDAY_ORDER.filter((day) => form.days.includes(day)).map((day) => t(DAY_KEY[day]))
  const nextDay = crossesMidnight(form.entryTime, form.exitTime)
  return (
    <>
      <span className="shabbat-confirm-line">{t('confirmManualDays', { days: days.join(', ') })}</span>
      <span className="shabbat-confirm-line">{t('confirmManualEntry', { time: form.entryTime })}</span>
      <span className="shabbat-confirm-line">
        {nextDay
          ? t('confirmManualExitNextDay', { time: form.exitTime })
          : t('confirmManualExit', { time: form.exitTime })}
      </span>
      {/* Whether these days and clocks cover now is `nextManualWindow`'s walk, which is not
          mirrored here (`windowInProgress`), so the release is stated conditionally — after the
          schedule, because the condition is about the lines above it. */}
      {latchedByAutomation ? (
        <span className="shabbat-confirm-line">{t('confirmReleaseIfNotNow')}</span>
      ) : null}
    </>
  )
}

/**
 * D174 — the offset caption, in words.
 *
 * **It is the accessible half of the sign.** The stepper shows the magnitude coloured — green for
 * later, red for earlier — and drops the minus sign, so colour alone would be carrying the
 * direction. This sentence says it in text: "10 דקות לפני כניסה", "15 דקות אחרי יציאה". Zero gets
 * its own wording rather than "0 דקות אחרי", which is true and reads like a bug.
 */
function offsetCaption(
  t: ReturnType<typeof useTranslation<'shabbat'>>['t'],
  edge: 'entry' | 'exit',
  value: number,
): string {
  if (value === 0) return t(edge === 'entry' ? 'offsetEntryExact' : 'offsetExitExact')
  const key =
    edge === 'entry'
      ? value < 0
        ? 'offsetEntryBefore'
        : 'offsetEntryAfter'
      : value < 0
        ? 'offsetExitBefore'
        : 'offsetExitAfter'
  return t(key, { count: Math.abs(value) })
}

/**
 * One ± stepper with its caption.
 *
 * **D174 replaced the signed number with a coloured magnitude.** `-10` next to a label reading
 * "היסט כניסה (דקות)" made a reader do two things at once: parse a sign, and work out which
 * direction "negative" is for an edge that is itself called "entry". The number is now unsigned
 * and coloured — later is green, earlier is red — and `caption` says the whole thing in words
 * underneath, which is also what keeps the direction off colour alone.
 */
function OffsetRow({
  label,
  value,
  caption,
  disabled,
  decreaseLabel,
  increaseLabel,
  onAdjust,
}: {
  readonly label: string
  readonly value: number
  readonly caption: string
  readonly disabled: boolean
  readonly decreaseLabel: string
  readonly increaseLabel: string
  /**
   * **A delta, not the next value** — and that is a fix, not a refactor. This used to hand the
   * parent `value - 5` computed from the rendered prop, so several taps inside one React batch
   * all read the same `value` and collapsed into a single step: three taps on `+` moved the
   * offset by 5, not 15. The parent now applies the delta inside its functional `setForm`, where
   * each update sees the previous one.
   */
  onAdjust(delta: number): void
}): JSX.Element {
  const direction = value === 0 ? 'zero' : value > 0 ? 'positive' : 'negative'
  return (
    <div className="shabbat-offset-block">
      <div className="shabbat-offset-row">
        <span className="gate-row-label">{label}</span>
        <div className="shabbat-offset-stepper">
          <button
            type="button"
            className="shabbat-offset-btn"
            aria-label={decreaseLabel}
            disabled={disabled}
            onClick={() => onAdjust(-OFFSET_STEP_MINUTES)}
          >
            −
          </button>
          {/* `aria-hidden`, and deliberately: unsigned, the number is half the value. The caption
              below carries the whole of it and is what a screen reader should read. */}
          <span className={`shabbat-offset-value ${direction}`} aria-hidden="true">
            {Math.abs(value)}
          </span>
          <button
            type="button"
            className="shabbat-offset-btn"
            aria-label={increaseLabel}
            disabled={disabled}
            onClick={() => onAdjust(OFFSET_STEP_MINUTES)}
          >
            +
          </button>
        </div>
      </div>
      <p className={`shabbat-offset-caption ${direction}`}>{caption}</p>
    </div>
  )
}
